import * as React from 'react';
import type User from 'benchmarking-domain/user';
import { authClient } from 'lib/auth/client';
import { logger } from 'lib/default-logger';
import { useIdleTimer } from 'react-idle-timer';

export interface UserContextValue {
  user: User | null;
  error: string | null;
  isLoading: boolean;
  pendingEmailConfirmation: boolean;
  checkSession?: () => Promise<void>;
}

export const UserContext = React.createContext<UserContextValue | undefined>(undefined);

export interface UserProviderProps {
  children: React.ReactNode;
}

export function UserProvider({ children }: UserProviderProps): React.JSX.Element {
  const [state, setState] = React.useState<{
    user: User | null;
    error: string | null;
    isLoading: boolean;
    pendingEmailConfirmation: boolean;
  }>({
    user: null,
    error: null,
    isLoading: true,
    pendingEmailConfirmation: false,
  });

  useIdleTimer({
    onIdle: async () => {
      logger.debug('Inactivity recognized.');
      await authClient.signOut();
      await checkSession();
    },
    timeout: 1_200_000,
    throttle: 500,
  });

  const checkSession = React.useCallback(async (): Promise<void> => {
    try {
      const { data, error, pendingEmailConfirmation } = await authClient.getUser();

      if (error) {
        logger.error(error);
        setState((prev) => ({
          ...prev,
          user: null,
          pendingEmailConfirmation,
          error: error,
          isLoading: false,
        }));
        return;
      }

      setState((prev) => ({ ...prev, user: data ?? null, pendingEmailConfirmation, error: null, isLoading: false }));
    } catch (err) {
      logger.error(err);
      setState((prev) => ({ ...prev, user: null, error: 'Something went wrong', isLoading: false }));
    }
  }, []);

  React.useEffect(() => {
    checkSession().catch((err: unknown) => {
      logger.error(err);
      // noop
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Expected
  }, []);

  return <UserContext.Provider value={{ ...state, checkSession }}>{children}</UserContext.Provider>;
}

export const UserConsumer = UserContext.Consumer;
